import { Component } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { ConfigStatut, InvestisseurType } from '@lib/models/generated/graphql';
import { OuiButtonToggleOption } from 'omnium-ui/button-toggle';
import { OuiDialogService } from 'omnium-ui/dialog';
import { OuiSelectOption } from 'omnium-ui/form-field';
import { AddLegacyDocDialogComponent } from 'projects/fiche-conseil-backoffice/src/app/components/admin-page/admin-fiche/add-legacy-doc-dialog/add-legacy-doc-dialog.component';
import {
  ConfigVersionColumn,
  EnveloppeColumn,
  FicheConfigColumn,
  HabilitationColumn,
  LibelleColumn,
  MaJColumn,
  NatureDashboardColumn,
  PersonneColumn,
  TypeColumn,
} from 'projects/fiche-conseil-backoffice/src/app/components/admin-page/admin-fiche/fiche-config-table/fiche-config-columns';
import {
  AdminConfig,
  AdminConfigList,
  AdminConfigService,
  FicheConfigType,
} from 'projects/fiche-conseil-backoffice/src/app/services/admin-config.service';
import { ReferentielService } from 'projects/fiche-conseil-backoffice/src/app/services/referentiel.service';

@Component({
  selector: 'app-fiche-dashboard',
  templateUrl: './fiche-dashboard.component.html',
  styleUrls: ['./fiche-dashboard.component.scss'],
})
export class FicheDashboardComponent {
  readonly FICHE_CONFIG_TOUTES = 1000;
  ficheTypeTogglecontrol: FormControl<number> = new FormControl<number>(this.FICHE_CONFIG_TOUTES, {
    nonNullable: true,
  });
  ficheTypeOptions: OuiButtonToggleOption<number>[] = [
    { value: this.FICHE_CONFIG_TOUTES, label: 'Toutes' },
    { value: FicheConfigType.Digitale, label: 'Digitale' },
    { value: FicheConfigType.AvecDoc, label: 'Avec doc.' },
    { value: FicheConfigType.SansDoc, label: 'Sans doc.' },
  ];
  enveloppesFilterOptions: OuiSelectOption<number>[] = []; //TODO
  enveloppesFilterControl: FormControl<number[] | null> = new FormControl<number[]>([]);
  investisseurTypeFilterOptions: OuiSelectOption<InvestisseurType>[] = [];
  investisseurTypeFilterControl: FormControl<InvestisseurType | null> = new FormControl<InvestisseurType | null>(null);
  natureFilterOptions: OuiSelectOption<number>[] = [];
  natureFilterControl: FormControl<number[] | null> = new FormControl<number[]>([]);
  requestResult: { data: { items: AdminConfig[]; totalCount: number }; loading: boolean } | undefined;
  isLoading: boolean = false;
  columnSelection: FicheConfigColumn[] = [
    new TypeColumn(),
    new EnveloppeColumn(),
    new PersonneColumn(),
    new NatureDashboardColumn(),
    new HabilitationColumn(),
    new LibelleColumn(),
    //new TemplateColumn(),
    new ConfigVersionColumn(),
    new MaJColumn(),
  ];

  constructor(
    private adminConfigService: AdminConfigService,
    private referentielService: ReferentielService,
    private dialogService: OuiDialogService,
    private router: Router
  ) {
    this.adminConfigService.adminConfigsSubject.subscribe(result => {
      this.setAllAdminConfigs(result);
    });

    //init options
    this.referentielService.getEnveloppes().then(enveloppes => {
      this.enveloppesFilterOptions =
        enveloppes?.map(enveloppe => ({
          value: enveloppe.code,
          label: enveloppe.libelle ?? enveloppe.code,
        })) ?? [];
    });

    this.investisseurTypeFilterOptions = [
      { value: InvestisseurType.PersonnePhysique, label: 'Personne physique' },
      { value: InvestisseurType.PersonneMorale, label: 'Personne morale' },
    ];

    this.referentielService.getNaturesOperations().then(natures => {
      this.natureFilterOptions =
        natures?.map(nature => ({
          value: nature.id,
          label: nature.libelle ?? nature.id.toString(),
        })) ?? [];
    });

    //update fiche on filters change
    this.ficheTypeTogglecontrol.valueChanges.subscribe(() => this.fetchAllConfigsAndApplyFilters());
    this.enveloppesFilterControl.valueChanges.subscribe(() => this.fetchAllConfigsAndApplyFilters());
    this.investisseurTypeFilterControl.valueChanges.subscribe(() => this.fetchAllConfigsAndApplyFilters());
    this.natureFilterControl.valueChanges.subscribe(() => this.fetchAllConfigsAndApplyFilters());

    this.fetchAllConfigsAndApplyFilters();
  }

  onConfigClick(config: AdminConfig) {
    if (config.type === FicheConfigType.SansDoc || config.type === FicheConfigType.AvecDoc) {
      const typeRef = this.dialogService.openDialog(
        AddLegacyDocDialogComponent,
        {
          ficheConfig: config,
        },
        'auto',
        '616px'
      );
      typeRef.afterClosed().subscribe(result => {
        //nothing to do
      });
    } else if (config.type === FicheConfigType.Digitale) {
      this.router.navigate(['/admin/fiches/details', config?.conditionsOfApplicability?.id]);
    }
  }

  withFilters(configs: AdminConfig[]): AdminConfig[] {
    return configs.filter(config => {
      let shouldKeepConfig = true;

      //Filter by type
      if (this.ficheTypeTogglecontrol.value !== this.FICHE_CONFIG_TOUTES) {
        shouldKeepConfig = shouldKeepConfig && config.type == this.ficheTypeTogglecontrol.value;
      }

      //Filter by enveloppe
      if (this.enveloppesFilterControl.value && this.enveloppesFilterControl.value.length > 0) {
        shouldKeepConfig =
          shouldKeepConfig &&
          this.enveloppesFilterControl.value.includes(config?.conditionsOfApplicability?.enveloppe?.code);
      }

      //Filter by investisseur type
      if (this.investisseurTypeFilterControl.value && this.investisseurTypeFilterControl.value.length > 0) {
        shouldKeepConfig =
          shouldKeepConfig &&
          this.investisseurTypeFilterControl.value.includes(config?.conditionsOfApplicability?.typeInvestisseur);
      }

      //Filter by investisseur type
      if (this.natureFilterControl.value && this.natureFilterControl.value.length > 0) {
        shouldKeepConfig =
          shouldKeepConfig &&
          this.natureFilterControl.value.includes(
            config?.conditionsOfApplicability?.natureOperation?.id ?? -Number.NaN
          );
      }

      return shouldKeepConfig;
    });
  }

  setAllAdminConfigs(configs: AdminConfigList) {
    if (configs) {
      let filteredConfigs = this.withFilters(configs.configs).filter(
        config => config.statut === ConfigStatut.Active || config.type === FicheConfigType.SansDoc
      );

      this.requestResult = {
        data: { items: filteredConfigs, totalCount: filteredConfigs.length ?? 0 },
        loading: configs.loading,
      };
      this.isLoading = configs.loading;
    } else {
      this.requestResult = { data: { items: [], totalCount: 0 }, loading: false };
      this.isLoading = false;
    }
  }

  fetchAllConfigsAndApplyFilters() {
    this.isLoading = true;
    this.requestResult = { data: { items: [], totalCount: 0 }, loading: true };
    this.adminConfigService.fetchAllConfigs();
  }
}
